/**
 * SafeArea — reads the device safe-area insets (iOS notch, Android gesture bar, etc.)
 * for use in Phaser scenes that can't directly consume CSS `env(safe-area-inset-*)`.
 *
 * Values are in CSS pixels. The result is cached and refreshed on window resize
 * and orientation change.
 *
 * Usage:
 *   import { getSafeAreaInsets } from '../../utils/SafeArea';
 *   const { top, bottom } = getSafeAreaInsets();
 *   chatPanel.setPosition(0, height - 40 - bottom);
 */

export interface SafeAreaInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

let cached: SafeAreaInsets | null = null;
let listenersInstalled = false;

function readInsets(): SafeAreaInsets {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  // Hidden probe that reads the four env() values via computed padding.
  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'width:0',
    'height:0',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top)',
    'padding-right:env(safe-area-inset-right)',
    'padding-bottom:env(safe-area-inset-bottom)',
    'padding-left:env(safe-area-inset-left)',
  ].join(';');

  document.body.appendChild(probe);
  const style = window.getComputedStyle(probe);
  const insets: SafeAreaInsets = {
    top: parseFloat(style.paddingTop) || 0,
    right: parseFloat(style.paddingRight) || 0,
    bottom: parseFloat(style.paddingBottom) || 0,
    left: parseFloat(style.paddingLeft) || 0,
  };
  document.body.removeChild(probe);
  return insets;
}

function installListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  const refresh = () => { cached = null; };
  window.addEventListener('resize', refresh, { passive: true });
  window.addEventListener('orientationchange', refresh, { passive: true });
  listenersInstalled = true;
}

/**
 * Returns the current safe-area insets.
 * Cached — repeated calls within a single layout are cheap.
 */
export function getSafeAreaInsets(): SafeAreaInsets {
  installListeners();
  if (!cached) cached = readInsets();
  return cached;
}

/**
 * Force a fresh read (bypass cache). Useful right after a known viewport change.
 */
export function refreshSafeAreaInsets(): SafeAreaInsets {
  cached = readInsets();
  return cached;
}
